/**
 * Build the clinic's shortform from worksheet state.
 *
 * This is a faithful reproduction of the notation on the back of their PLAN INFO
 * sheet — the artifact the front desk actually reads. Their rules, kept:
 *
 *   - exceptions come FIRST, each on its own line, prefixed with a star;
 *   - the body is a dense comma-run of abbreviations (FG, SpFee, OE&Pan, LTM…);
 *   - dependent + FT student ages get their own line;
 *   - the last line is the date, who verified it, and the account number used.
 *
 * Blank fields are OMITTED rather than printed empty: their sheet never carries
 * "Sealant ___" once written up, and a shortform padded with blanks is harder to
 * scan than a short one. `missing()` reports what was skipped so the UI can show
 * a completeness hint instead of the reader having to notice an absence.
 */

import { FAMILIES } from "./sampleData";

/** "24" -> "24M"; empty stays empty. */
function months(v) {
  const s = String(v == null ? "" : v).trim();
  return s ? s + "M" : "";
}

function yn(v) {
  const s = String(v == null ? "" : v).trim().toUpperCase();
  return s === "Y" || s === "N" ? s : "";
}

/**
 * The shortform's body lines, in their order. Returns an array of strings so the
 * caller can render them as separate lines (and so a hand-edit can replace one
 * line without disturbing the rest).
 */
export function buildShortformLines(state) {
  const plan = state.plan || {};
  const money = state.money || {};
  const elig = state.eligibility || {};
  const hyg = state.hygiene || {};
  const fam = state.families || {};
  const ortho = fam.ortho || {};

  const lines = [];

  // 1. Exceptions, starred, always first — collected from the FAMILIES that own
  // them, each labelled so the reader knows what it qualifies. Their sheet puts
  // exceptions on line one for the same reason: the abnormal outranks the normal.
  FAMILIES.forEach(function (f) {
    const entry = fam[f.key] || {};
    const text = String(entry.exception || "").trim();
    if (text) lines.push("*" + f.name + ": " + text);
  });

  // 2. The dense body run.
  const bits = [];
  if (money.feeGuide) bits.push("FG" + (money.feeGuide === "C" ? "-C" : money.feeGuide));
  if (yn(money.specialistFee)) bits.push("SpFee-" + yn(money.specialistFee));
  if (yn(hyg.compositesMolars)) bits.push("23322-" + yn(hyg.compositesMolars));
  if (yn(hyg.sealants)) bits.push("Sealant-" + yn(hyg.sealants));

  // OE and Pan collapse to one token when they share an interval — "OE&Pan/24M"
  // is how their example writes it.
  const oe = months(hyg.oeMonths);
  const pan = months(hyg.panMonths);
  if (oe && pan && oe === pan) bits.push("OE&Pan/" + oe);
  else {
    if (oe) bits.push("OE/" + oe);
    if (pan) bits.push("Pan/" + pan);
  }
  if (months(hyg.bwMonths)) bits.push("BW/" + months(hyg.bwMonths));
  if (yn(hyg.ohi)) bits.push("OHI-" + yn(hyg.ohi));
  // Reline is billed under the Denture family but the carrier is asked about it
  // separately (their form prints "Reline Y/N ___%"), so it carries its own
  // field rather than inheriting the family's percentage.
  if (hyg.relinePct) bits.push("Reline " + hyg.relinePct + "%");

  const orthoBits = [];
  if (ortho.pct) orthoBits.push(ortho.pct + "%");
  if (ortho.orthoLTM) orthoBits.push("$" + ortho.orthoLTM + " / LTM");
  if (ortho.orthoAgeLimit) orthoBits.push("under " + ortho.orthoAgeLimit + "Y");
  if (orthoBits.length) bits.push("Ortho " + orthoBits.join(" "));

  if (bits.length) lines.push(bits.join(", ") + ".");

  // 3. Dependant / FT student ages.
  const ages = [];
  if (elig.dependentAge) ages.push("Dep under " + elig.dependentAge + "Y.");
  if (elig.ftStudentAge) ages.push("FT Student under " + elig.ftStudentAge + "Y.");
  if (ages.length) lines.push(ages.join("  "));

  // 4. Provenance: when, who, and the account number the info was pulled under.
  const prov = [];
  if (state.verifiedOn) prov.push(String(state.verifiedOn).replace(/\s+/g, ""));
  if (state.verifiedBy) prov.push(state.verifiedBy);
  if (plan.accountNumber) prov.push("(#" + plan.accountNumber + ")");
  if (prov.length) lines.push(prov.join(" "));

  return lines;
}

export function buildShortform(state) {
  return buildShortformLines(state).join("\n");
}

/**
 * Fields the shortform would normally carry that are still blank. Drives the
 * "still missing" hint — the whole risk with a compact summary is that an
 * unanswered question and an unimportant one look identical.
 */
export function missingShortformFields(state) {
  const money = state.money || {};
  const elig = state.eligibility || {};
  const hyg = state.hygiene || {};
  const out = [];
  const want = [
    [money.feeGuide, "Fee guide"],
    [money.specialistFee, "Specialist fee"],
    [hyg.compositesMolars, "Composites on molars"],
    [hyg.sealants, "Sealants"],
    [hyg.oeMonths, "Oral exam interval"],
    [hyg.panMonths, "Pan interval"],
    [hyg.bwMonths, "Bitewing interval"],
    [hyg.ohi, "OHI"],
    [elig.dependentAge, "Dependant age"],
    [elig.ftStudentAge, "FT student age"],
  ];
  want.forEach(function (pair) {
    const v = String(pair[0] == null ? "" : pair[0]).trim();
    if (!v) out.push(pair[1]);
  });
  return out;
}
